import { useEffect, useState } from 'react';
import {
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  fetchRecordEvents,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { ProblemDetail, RecordEventItem } from '../../../types/syntiro_honey';

const EVENT_COLOR: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  EXTRACTED: 'info',
  EDITED: 'default',
  APPROVED: 'success',
  REJECTED: 'error',
  FLAGGED: 'warning',
  REASSIGNED: 'info',
  REOPENED: 'warning',
  SUPERSEDED: 'warning'
};

/** "canonicalUnit" → "Canonical unit"; "metricId" → "Metric". */
function humaniseField(field: string): string {
  const spaced = field
    .replace(/Id$/, '')
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Renders one side of a change as reviewer-facing text. */
function describeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    // A few event kinds carry an object rather than a scalar (EXTRACTED,
    // SUPERSEDED, and the synthetic `metricAlias` field). Show the pairs as
    // prose rather than raw JSON — this trail is read by reviewers.
    return Object.entries(value as Record<string, unknown>)
      .map(([key, entry]) => `${humaniseField(key)}: ${describeValue(entry)}`)
      .join(' · ');
  }
  return String(value);
}

/**
 * Renders an event's change as "old → new", collapsing the cases where only one
 * side is meaningful (creation has no old value; a flag toggle reads better as
 * just the new state).
 * @param {RecordEventItem} event The audit-trail entry.
 * @returns {string} The formatted change, or an em dash when there is nothing to show.
 */
export function describeChange(event: RecordEventItem): string {
  const before = describeValue(event.oldValue);
  const after = describeValue(event.newValue);
  if (before === '—' && after === '—') {
    return '—';
  }
  if (before === '—') {
    return after;
  }
  return `${before} → ${after}`;
}

interface RecordHistoryListProps {
  /** The record whose audit trail to show. */
  recordId: string;
}

/**
 * The record's audit trail: every append-only RecordEvent, attributed and
 * timestamped, oldest first. This is the reviewer-facing view of the log that is
 * the SOURCE OF TRUTH for how the record reached its current state — the
 * record-level complement of the admin FileAuditDialog.
 * @returns {JSX.Element}
 */
export default function RecordHistoryList({ recordId }: RecordHistoryListProps) {
  const [events, setEvents] = useState<Array<RecordEventItem> | null>(null);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  useEffect(() => {
    // Stale-response guard: for an AUDIT trail specifically, record A's late
    // response landing under record B would misattribute the one artefact that
    // exists to establish who did what.
    let cancelled = false;
    setEvents(null);
    setProblem(null);
    fetchRecordEvents(recordId)
      .then((result) => {
        if (!cancelled) {
          setEvents(result);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setProblem(toProblemDetail(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  if (problem !== null) {
    return <Alert severity="error">{problem.detail ?? problem.title ?? 'Loading failed.'}</Alert>;
  }

  if (events === null) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (events.length === 0) {
    return <CaptionText>No events have been recorded for this record.</CaptionText>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Event</TableCell>
          <TableCell>Field</TableCell>
          <TableCell>Change</TableCell>
          <TableCell>Actor</TableCell>
          <TableCell>When</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {events.map((event, index) => (
          <TableRow
            // Several events can share a type+timestamp (one edit writes one
            // event PER changed field), so the index is part of the identity.
            key={`${event.type}-${event.field ?? ''}-${event.createdAt}-${index}`}
          >
            <TableCell>
              <Chip
                size="small"
                label={event.type}
                color={EVENT_COLOR[event.type] ?? 'default'}
                variant="outlined"
              />
            </TableCell>
            <TableCell>{event.field === null ? '—' : humaniseField(event.field)}</TableCell>
            <TableCell>
              <CaptionText>{describeChange(event)}</CaptionText>
            </TableCell>
            {/* A uid identifies nobody to a reviewer; the uid stays as the fallback
                so trails written before the API resolved names remain attributed. */}
            <TableCell>{event.actorName ?? event.actorId}</TableCell>
            <TableCell>{new Date(event.createdAt).toLocaleString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
